import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
	Card,
	Col,
	CardBody,
	CardFooter,
	Table,
	Modal,
	ModalHeader,
	ModalBody,
} from "reactstrap";
import {
	H3,
	H6,
	P,
	Spinner,
	Badges,
} from "AbstractElements";
import { setFilters } from "Features/Learning/Users/historyUserReferralCompactSlice";
import {
	fetchHistoryReferralsCompacts,
	handleBackPagination,
	handleNextPagination,
	handleClickPagination,
} from "./ReferrerListUtils";
import CurrencyFormat from "Component/Learning/Common/GenerateContent/CurrencyFormatter";
import PaginationComponent from "Component/Learning/Common/Pagination";
import Moment from "moment";
import "moment/locale/id";
import { UserServices } from "Services/axios/User.Service";
import { handleApiError } from "Features/Common/ErrorHandler";

const ReferrerListTable = () => {
	const dispatch = useDispatch();
	const filters = useSelector(
		(state) => state.historyUserReferralCompact.filters
	);
	const isLoading = useSelector(
		(state) => state.historyUserReferralCompact.isLoading
	);
	const cards = useSelector(
		(state) => state.historyUserReferralCompact.data.data
	);
	const pagination = useSelector(
		(state) => state.historyUserReferralCompact.data.pagination
	);
	const [refatch, setRefatch] = useState(false);
	const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
	const [selectedReferrer, setSelectedReferrer] = useState(null);
	const [referralUsers, setReferralUsers] = useState([]);
	const [isLoadingReferralUsers, setIsLoadingReferralUsers] = useState(false);

	const toggleReferralModal = () => {
		setIsReferralModalOpen((prevState) => !prevState);
	};

	const handleOpenReferralModal = async (item) => {
		setSelectedReferrer(item);
		setReferralUsers([]);
		setIsReferralModalOpen(true);
		setIsLoadingReferralUsers(true);

		try {
			// Ambil daftar user referee berdasarkan id referrer yang diklik.
			const response = await UserServices.getAllReferralUser(item.id);
			setReferralUsers(response?.data?.data || []);
		} catch (error) {
			// Error handler global melempar ulang error, jadi ditahan agar tidak jadi unhandled rejection.
			try {
				handleApiError(error);
			} catch (handledError) {
				// no-op
			}
		} finally {
			setIsLoadingReferralUsers(false);
		}
	};

	useEffect(() => {
		fetchHistoryReferralsCompacts(dispatch, filters, refatch);
		window.scrollTo(0, 0);
	}, [dispatch, filters, refatch]);

	return (
		<>
			{isLoading ? (
				<Col lg="12">
					<div className="loader-box">
						<Spinner attrSpinner={{ className: "loader-30" }} />
					</div>
					<H6 attrH6={{ className: "sub-title mb-0 text-center" }}>
						{"Data Loading..."}
					</H6>
				</Col>
			) : (
				cards &&
				cards.length > 0 && (
					<Col sm="12 order-2">
						<Card>
							<CardBody>
								<Table className="table-bordered table-striped table-hover" responsive={true}>
									<thead>
										<tr>
											<th
												scope="col"
												className="bg-primary text-light"
												style={{ textAlign: "center", padding: "8px 8px" }}>
												#
											</th>
											<th
												scope="col"
												className="bg-primary text-light"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Nama Referrer
											</th>
											<th
												scope="col"
												className="bg-primary text-light text-center"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Saldo
											</th>
											<th
												scope="col"
												className="bg-primary text-light text-center"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Jumlah Order
											</th>
											<th
												scope="col"
												className="bg-primary text-light text-center"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Jumlah Referee
											</th>
											<th
												scope="col"
												className="bg-primary text-light text-center"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Total Komisi
											</th>
											<th
												scope="col"
												className="bg-primary text-light text-center"
												style={{ textAlign: "center", padding: "8px 10px" }}>
												Action
											</th>
										</tr>
									</thead>
									<tbody>
										{cards.map((item, index) => (
											<tr key={index}>
												<th style={{ textAlign: "center", padding: "8px 8px" }}>{pagination.from + index}</th>
												<td style={{ padding: "8px 10px" }}>
													{item.name}
													<Link
														to={`${process.env.PUBLIC_URL}/users/user-detail/${item.id}`}
														title="Lihat detail user"
														className="p-1 ms-2">
														<i className="fa fa-info-circle mx-auto fa-lg"></i>
													</Link>
													({item.referral_code})
													<br />
													<b>
														<a
															href={`https://wa.me/62${item.phone}?text=Halo%20kak%20${item.name},%20saya%20ingin%20bertanya%20terkait..`}
															target="_blank"
															rel="noreferrer"
															title="Hubungi via whatsapp">
															{item.phone.replace(
																/(\d{4})(\d{4})(\d{4})/,
																"$1 $2 $3"
															)}
														</a>
													</b>
												</td>
												<td className="text-center" style={{ padding: "8px 10px" }}>
													<Badges attrBadge={{ color: "primary" }}>
														<CurrencyFormat amount={item.referral_amount} />
													</Badges>
												</td>
												<td className="text-center" style={{ padding: "8px 10px" }}>
													<Link
														to={`${process.env.PUBLIC_URL}/users/referral-order/${item.id}`}
														title="Lihat detail order">
														<h4>
															<Badges attrBadge={{ color: "primary" }}>
																{item.order_paid}
															</Badges>
														</h4>
													</Link>
												</td>
												<td className="text-center" style={{ padding: "8px 10px" }}>
													<h4>
														<button
															type="button"
															onClick={() => handleOpenReferralModal(item)}
															className="btn btn-link p-0 border-0 text-decoration-none"
															title="Lihat daftar referee"
														>
															<Badges attrBadge={{ color: "warning" }}>
																{item.total_referral_user}
															</Badges>
														</button>
													</h4>
												</td>
												<td className="text-right" style={{ padding: "8px 10px" }}>
													<CurrencyFormat amount={item.sum_order_paid} />
												</td>
												<td className="text-center" style={{ padding: "8px 10px" }}>
													<Link
														to={`${process.env.PUBLIC_URL}/users/referral-mutation/${item.id}`}
														title="Lihat laporan komisi">
														<h4>
															<Badges attrBadge={{ color: "primary" }}>
																<i className="fa fa-info-circle mx-auto fa-lg"></i>
															</Badges>
														</h4>
													</Link>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</CardBody>
							<CardFooter>
								<PaginationComponent
									pagination={pagination}
									handleBackPagination={() =>
										handleBackPagination(
											pagination,
											filters,
											dispatch,
											setFilters,
											setRefatch
										)
									}
									handleNextPagination={() =>
										handleNextPagination(
											pagination,
											filters,
											dispatch,
											setFilters,
											setRefatch
										)
									}
									handleClickPagination={(page) =>
										handleClickPagination(
											page,
											filters,
											dispatch,
											setFilters,
											setRefatch
										)
									}
								/>
							</CardFooter>
						</Card>
					</Col>
				)
			)}

			{(!cards || cards.length === 0) && isLoading === false && (
				<Col lg="12">
					<H3 attrH3={{ className: "sub-title mb-5 text-center" }}>
						{!cards ? "Sumber Data Tidak Tersedia" : "Data Tidak Ditemukan"}
					</H3>
				</Col>
			)}

			<Modal isOpen={isReferralModalOpen} toggle={toggleReferralModal} size="lg">
				<ModalHeader toggle={toggleReferralModal}>
					Daftar Referee {selectedReferrer?.name ? `- ${selectedReferrer.name}` : ""}
				</ModalHeader>
				<ModalBody>
					{isLoadingReferralUsers ? (
						<div className="text-center py-4">
							<Spinner attrSpinner={{ className: "loader-30" }} />
							<P attrPara={{ className: "mt-2 mb-0" }}>Memuat data referee...</P>
						</div>
					) : referralUsers.length === 0 ? (
						<P attrPara={{ className: "mb-0 text-center" }}>
							Belum ada data referee untuk user ini.
						</P>
					) : (
						<Table className="table-bordered table-striped" responsive={true}>
							<thead>
								<tr>
									<th className="text-center">#</th>
									<th>Nama</th>
									<th>Email</th>
									<th>No. HP</th>
									<th className="text-center">Tanggal Daftar</th>
								</tr>
							</thead>
							<tbody>
								{referralUsers.map((referee, index) => (
									<tr key={referee.id || index}>
										<td className="text-center">{index + 1}</td>
										<td>{referee.name || "-"}</td>
										<td>{referee.email || "-"}</td>
										<td>{referee.phone || "-"}</td>
										<td className="text-center">
											{referee.created_at
												? Moment(referee.created_at).format("DD MMM YYYY HH:mm")
												: "-"}
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</ModalBody>
			</Modal>
		</>
	);
};

export default ReferrerListTable;